import {callAPI} from '@/store/utils';

const FYGR_COMPANY = 'FYGR_COMPANY';

export default {
  namespaced: true,
  state: {
    invoiceDetails: [],
    // Suggestions de rapprochement en attente (TT-7250). Le compteur vit dans
    // le store et non dans InvoicesWrapper : <router-view :key="$route.fullPath">
    // (App.vue) remonte la page à chaque bascule d'onglet, et un compteur local
    // repartirait de 0 — l'onglet « Réconciliation » disparaîtrait puis
    // réapparaîtrait à chaque changement d'onglet.
    etsSuggestionsPending: 0,
    // `etsSuggestionsPending` compte des *paires*, toutes natures confondues ;
    // la puce compte des *factures* par onglet. Les deux divergent.
    etsSuggestionCounts: null,
  },
  getters: {
    getterInvoiceDetails(state) {
      return state.invoiceDetails;
    },
    getterETSSuggestionsPending(state) {
      return state.etsSuggestionsPending;
    },
    // nature : 'income' (Clients) | 'expense' (Fournisseurs).
    getterETSSuggestionInvoiceCount(state) {
      return (nature) =>
        Number(state.etsSuggestionCounts?.by_nature?.[nature]?.pending_invoice_count) ||
        0;
    },
    // Des *transactions*, là où `etsSuggestionsPending` compte des paires.
    getterETSSuggestionTransactionCount(state) {
      return (
        Number(state.etsSuggestionCounts?.pending_transaction_count) || 0
      );
    },
  },
  mutations: {
    setInvoiceDetails(state, data) {
      state.invoiceDetails = data;
    },
    setETSSuggestionsPending(state, count) {
      state.etsSuggestionsPending = Number(count) || 0;
    },
    setETSSuggestionCounts(state, result) {
      state.etsSuggestionCounts = result || null;
    },
  },
  actions: {
    async actionGetInvoicesTemplate() {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getInvoicesTemplate', {companyId});
      return res.result;
    },
    async actionAddManualInvoices(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'addManualInvoices',
        {...params, companyId},
        false,
        120,
        0,
      );
      return res;
    },
    async actionAddInvoicesViaPartner(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'uploadDocument',
        {...params, companyId},
        false,
        120,
        0,
      );
      return res;
    },
    async actionUploadInvoices(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        'uploadInvoices',
        {...params, companyId},
        false,
        120,
        0,
      );
      return res;
    },
    async actionGetInvoicesHeaders(_, params) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getInvoicesHeaders', {
        ...params,
        companyId,
      });
      return res;
    },
    async actionCreateInvoice(_, invoice) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const copy = {...invoice, company_id: companyId};
      delete copy.needChangeETS;
      delete copy.ets;
      const res = await callAPI('POST', 'createInvoice', {
        invoice: copy,
        companyId,
      });
      return res;
    },
    async actionUpdateInvoice(_, invoice) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const should_invalidate_cache = invoice.should_invalidate_cache;
      const copy = {...invoice, company_id: companyId};
      delete copy.needChangeETS;
      delete copy.should_invalidate_cache;
      delete copy.ets;
      const req = {
        invoice: copy,
        companyId,
        ...(should_invalidate_cache !== null && {
          should_invalidate_cache,
        }),
      };
      const res = await callAPI('POST', 'updateInvoice', req);
      return res;
    },
    async actionPartialUpdateInvoice(_, invoice) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('PATCH', 'updateInvoices', {
        ...invoice,
        companyId,
      });
      return res;
    },
    async actionGetInvoices({dispatch}, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'listInvoices', {
        ...request,
        companyId,
      });
      // Le compteur de suggestions est rafraîchi avec chaque vraie requête de
      // liste (bascule d'onglet, recherche, filtre) — mais pas sur les pages
      // suivantes de l'infinite scroll. Non attendu : ne doit jamais retarder
      // l'affichage des factures.
      if (!request?.offset) {
        dispatch('actionCountETSSuggestions').catch(() => {});
      }
      return res.result;
    },
    async actionDeleteInvoice(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'deleteInvoice', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionUpdateEngagedInvoices(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'updateEngagedInvoices', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionSetInvoicesCategory(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setInvoicesCategory', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionCreateETS(_, ets) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const {should_invalidate_cache, ...copy} = {...ets};
      delete copy.ets_transactions;
      delete copy.newOne;
      delete copy.updatedOne;
      const res = await callAPI('POST', 'createETS', {
        ets: copy,
        companyId,
        should_invalidate_cache,
      });
      return res;
    },
    async actionUpdateETS(_, ets) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const {should_invalidate_cache, ...copy} = {...ets};
      delete copy.etsTransactions;
      delete copy.newOne;
      delete copy.updatedOne;
      const res = await callAPI('POST', 'updateETS', {
        ets: copy,
        companyId,
        should_invalidate_cache,
      });
      return res;
    },
    async actionDeleteETS(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'deleteETS', {...request, companyId});
      return res;
    },
    async actionGetETS(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getETSByInvoiceId', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionGetInvoice(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getInvoice', {...request, companyId});
      return res.result;
    },
    async actionAttachTransaction(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'attachTransactionToETS', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionDetachTransaction(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'detachTransactionFromETS', {
        ...request,
        companyId,
      });
      return res;
    },
    // --- Suggestions de rapprochement ETS <-> transaction (TT-7250) ---
    // Ces actions renvoient l'enveloppe brute (comme actionGetETS) : les appelants
    // ont besoin de result.remaining_pending_count / ets_status / invoice_status.
    async actionCountETSSuggestions({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'countETSTransactionSuggestions', {
        companyId,
      });
      if (res && !res.error) {
        commit('setETSSuggestionsPending', res.result?.pending_count);
        commit('setETSSuggestionCounts', res.result);
      }
      return res;
    },
    // Compteur renvoyé par validate/reject : évite un aller-retour de plus.
    actionSetETSSuggestionsPending({commit}, count) {
      commit('setETSSuggestionsPending', count);
    },
    async actionGetETSSuggestions(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'listETSTransactionSuggestions', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionValidateETSSuggestion(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'validateETSTransactionSuggestion', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionRejectETSSuggestion(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'rejectETSTransactionSuggestion', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionCreateCheck(_, check) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const copy = {...check, company_id: companyId};
      delete copy.transaction;
      const res = await callAPI('POST', 'createBankCheck', {
        bank_check: copy,
        companyId,
      });
      return res;
    },
    async actionGetChecks(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'listBankChecks', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionUpdateCheck(_, check) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const copy = {...check};
      delete copy.transaction;
      const res = await callAPI('POST', 'updateBankCheck', {
        bank_check: copy,
        companyId,
      });
      return res;
    },
    async actionDeleteCheck(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'deleteBankCheck', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionGetETSByBankCheckId(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getETSByBankCheckId', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionGetInvoicesDetails(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getInvoicesDetails', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionGetBankChecksDetails(_, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getBankChecksDetails', {
        ...request,
        companyId,
      });
      return res;
    },
    async actionGetDetailsOfInvoiceETS({commit}, request) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      commit('setInvoiceDetails', []);
      const res = await callAPI('POST', 'getDetailsOfInvoiceETS', {
        ...request,
        companyId,
      });
      if (res && !res.error) {
        commit('setInvoiceDetails', res?.result);
        return res?.result;
      }
      return res;
    },
  },
};
